import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, Button } from "@repo/o37-ui";
import {
  ArrowLeft,
  TrendingUp,
  TrendingDown,
  ArrowRight,
  CheckCircle,
  AlertCircle,
  Minus
} from "lucide-react";
import { HoldingsCostPlan, HoldingsPlanLineItem, formatCents } from '../../types';

const API_URL = import.meta.env.DEV ? "http://localhost:3001/api" : "/api";

interface ComparisonData {
  currentMonthlyRunRate: number;
  plannedMonthlyRunRate: number;
  currentAnnualRunRate: number;
  plannedAnnualRunRate: number;
  monthlyDelta: number;
  annualDelta: number;
  percentChange: number;
  categoryBreakdown: {
    category: string;
    current: number;
    planned: number;
    delta: number;
  }[];
}

export default function PlanComparison() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [plan, setPlan] = useState<HoldingsCostPlan | null>(null);
  const [lineItems, setLineItems] = useState<HoldingsPlanLineItem[]>([]);
  const [comparison, setComparison] = useState<ComparisonData | null>(null);
  const [loading, setLoading] = useState(true);

  const userId = localStorage.getItem('app_user_id') || '';
  const userRole = localStorage.getItem('app_role') || 'user';

  useEffect(() => {
    if (id) loadData();
  }, [id]);

  const loadData = async () => {
    try {
      // Load plan details
      const planRes = await fetch(`${API_URL}/holdings/plans/${id}`, {
        headers: { 'x-user-id': userId, 'x-user-role': userRole }
      });

      let planData: any = null;

      if (planRes.ok) {
        planData = await planRes.json();
        setPlan({
          id: planData.id,
          name: planData.name,
          description: planData.description,
          timeHorizonMonths: planData.time_horizon_months,
          startDate: planData.start_date,
          endDate: planData.end_date,
          status: planData.status,
          createdAt: planData.created_at,
          updatedAt: planData.updated_at || planData.created_at
        });

        if (planData.line_items) {
          setLineItems(planData.line_items.map((li: any) => ({
            id: li.id,
            planId: li.plan_id,
            itemType: li.item_type,
            name: li.name,
            description: li.description,
            subscriptionId: li.subscription_id,
            monthlyImpactCents: li.monthly_impact_cents,
            annualImpactCents: li.annual_impact_cents,
            effectiveDate: li.effective_date,
            subscription: li.holdings_subscriptions ? {
              id: li.holdings_subscriptions.id,
              vendorName: li.holdings_subscriptions.vendor_name
            } : undefined
          })));
        }
      }

      // Load comparison data from API or calculate
      const compareRes = await fetch(`${API_URL}/holdings/plans/${id}/compare`, {
        headers: { 'x-user-id': userId, 'x-user-role': userRole }
      });

      if (compareRes.ok) {
        const compareData = await compareRes.json();
        setComparison(compareData);
      } else if (planData) {
        // Fallback: calculate locally from run rate data
        const runRateRes = await fetch(`${API_URL}/holdings/analytics/run-rate`, {
          headers: { 'x-user-id': userId, 'x-user-role': userRole }
        });

        if (runRateRes.ok) {
          const runRate = await runRateRes.json();
          const totalMonthlyImpact = planData.line_items?.reduce(
            (sum: number, li: any) => sum + (li.monthly_impact_cents || 0), 0
          ) || 0;

          setComparison({
            currentMonthlyRunRate: runRate.monthlyTotal || 0,
            plannedMonthlyRunRate: (runRate.monthlyTotal || 0) + totalMonthlyImpact,
            currentAnnualRunRate: runRate.annualTotal || 0,
            plannedAnnualRunRate: (runRate.annualTotal || 0) + (totalMonthlyImpact * 12),
            monthlyDelta: totalMonthlyImpact,
            annualDelta: totalMonthlyImpact * 12,
            percentChange: runRate.monthlyTotal > 0
              ? (totalMonthlyImpact / runRate.monthlyTotal) * 100
              : 0,
            categoryBreakdown: []
          });
        }
      }
    } catch (e) {
      console.error('Failed to load comparison data:', e);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getItemTypeLabel = (type: string) => {
    switch (type) {
      case 'new_subscription': return 'New';
      case 'modify_subscription': return 'Modify';
      case 'cancel_subscription': return 'Cancel';
      case 'headcount_change': return 'Headcount';
      case 'one_time_expense': return 'One-Time';
      default: return type;
    }
  };

  const getItemTypeColor = (type: string) => {
    switch (type) {
      case 'new_subscription': return 'bg-blue-100 text-blue-700';
      case 'modify_subscription': return 'bg-amber-100 text-amber-700';
      case 'cancel_subscription': return 'bg-red-100 text-red-700';
      case 'headcount_change': return 'bg-purple-100 text-purple-700';
      case 'one_time_expense': return 'bg-emerald-100 text-emerald-700';
      default: return 'bg-neutral-100 text-neutral-700';
    }
  };

  if (loading) {
    return (
      <div>
        <h1 className="text-2xl font-semibold mb-2">Plan Comparison</h1>
        <p className="text-neutral-500">Loading...</p>
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 mx-auto mb-4 text-neutral-300" />
        <h2 className="text-xl font-semibold mb-2">Plan Not Found</h2>
        <Button onClick={() => navigate('/holdings/planning')} text="Back to Plans" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate(`/holdings/planning/${id}`)}
          className="flex items-center gap-1.5 text-neutral-500 hover:text-neutral-700 mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Plan</span>
        </button>

        <h1 className="text-2xl font-semibold mb-1">Plan Comparison</h1>
        <p className="text-neutral-500">
          Comparing "{plan.name}" to current run rate
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        {/* Current */}
        <Card>
          <CardContent className="p-6">
            <h3 className="text-sm font-medium text-neutral-500 mb-2">Current Monthly Run Rate</h3>
            <p className="text-3xl font-semibold text-neutral-900">
              {formatCents(comparison?.currentMonthlyRunRate || 0)}
            </p>
            <p className="text-sm text-neutral-500 mt-1">
              {formatCents(comparison?.currentAnnualRunRate || 0)}/year
            </p>
          </CardContent>
        </Card>

        {/* Change */}
        <Card className={comparison?.monthlyDelta !== 0 ? (
          comparison?.monthlyDelta! > 0 ? 'border-red-200 bg-red-50' : 'border-emerald-200 bg-emerald-50'
        ) : ''}>
          <CardContent className="p-6">
            <h3 className="text-sm font-medium text-neutral-500 mb-2">Monthly Change</h3>
            <div className="flex items-center gap-2">
              {comparison?.monthlyDelta !== 0 && (
                comparison?.monthlyDelta! > 0 ? (
                  <TrendingUp className="w-6 h-6 text-red-500" />
                ) : (
                  <TrendingDown className="w-6 h-6 text-emerald-500" />
                )
              )}
              <p className={`text-3xl font-semibold ${
                comparison?.monthlyDelta! > 0 ? 'text-red-600' :
                comparison?.monthlyDelta! < 0 ? 'text-emerald-600' : 'text-neutral-900'
              }`}>
                {comparison?.monthlyDelta! > 0 ? '+' : ''}{formatCents(comparison?.monthlyDelta || 0)}
              </p>
            </div>
            <p className="text-sm text-neutral-500 mt-1">
              {comparison?.percentChange !== 0 && (
                <>
                  {comparison?.percentChange! > 0 ? '+' : ''}
                  {comparison?.percentChange?.toFixed(1)}% change
                </>
              )}
            </p>
          </CardContent>
        </Card>

        {/* Planned */}
        <Card>
          <CardContent className="p-6">
            <h3 className="text-sm font-medium text-neutral-500 mb-2">Planned Monthly Run Rate</h3>
            <p className="text-3xl font-semibold text-neutral-900">
              {formatCents(comparison?.plannedMonthlyRunRate || 0)}
            </p>
            <p className="text-sm text-neutral-500 mt-1">
              {formatCents(comparison?.plannedAnnualRunRate || 0)}/year
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Visual Comparison Bar */}
      <Card className="mb-8">
        <CardContent className="p-6">
          <h3 className="font-semibold text-neutral-900 mb-4">Visual Comparison</h3>
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-neutral-600">Current</span>
                <span className="text-sm font-medium">{formatCents(comparison?.currentMonthlyRunRate || 0)}</span>
              </div>
              <div className="h-8 bg-neutral-200 rounded-lg overflow-hidden">
                <div
                  className="h-full bg-neutral-500 rounded-lg"
                  style={{ width: '100%' }}
                />
              </div>
            </div>

            <ArrowRight className="w-6 h-6 text-neutral-400 flex-shrink-0" />

            <div className="flex-1">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-neutral-600">Planned</span>
                <span className="text-sm font-medium">{formatCents(comparison?.plannedMonthlyRunRate || 0)}</span>
              </div>
              <div className="h-8 bg-neutral-200 rounded-lg overflow-hidden">
                {comparison && comparison.currentMonthlyRunRate > 0 && (
                  <div
                    className={`h-full rounded-lg ${
                      comparison.plannedMonthlyRunRate > comparison.currentMonthlyRunRate
                        ? 'bg-red-500'
                        : comparison.plannedMonthlyRunRate < comparison.currentMonthlyRunRate
                        ? 'bg-emerald-500'
                        : 'bg-neutral-500'
                    }`}
                    style={{
                      width: `${Math.min(100, (comparison.plannedMonthlyRunRate / comparison.currentMonthlyRunRate) * 100)}%`
                    }}
                  />
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Line Items Breakdown */}
      <Card>
        <CardContent className="p-6">
          <h3 className="font-semibold text-neutral-900 mb-4">Planned Changes ({lineItems.length})</h3>

          {lineItems.length === 0 ? (
            <div className="text-center py-8 text-neutral-500">
              <Minus className="w-8 h-8 mx-auto mb-2 text-neutral-300" />
              <p>No line items in this plan</p>
            </div>
          ) : (
            <div className="space-y-3">
              {lineItems.map(item => (
                <div
                  key={item.id}
                  className="flex items-center justify-between p-4 bg-neutral-50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${getItemTypeColor(item.itemType)}`}>
                      {getItemTypeLabel(item.itemType)}
                    </span>
                    <div>
                      <div className="font-medium text-neutral-900">{item.name}</div>
                      {item.description && (
                        <div className="text-sm text-neutral-500">{item.description}</div>
                      )}
                      <div className="text-xs text-neutral-400">
                        Effective: {formatDate(item.effectiveDate)}
                      </div>
                    </div>
                  </div>

                  <div className="text-right">
                    <div className={`font-medium ${
                      item.monthlyImpactCents > 0 ? 'text-red-600' :
                      item.monthlyImpactCents < 0 ? 'text-emerald-600' : 'text-neutral-600'
                    }`}>
                      {item.monthlyImpactCents > 0 ? '+' : ''}{formatCents(item.monthlyImpactCents)}/mo
                    </div>
                    <div className="text-sm text-neutral-500">
                      {item.annualImpactCents > 0 ? '+' : ''}{formatCents(item.annualImpactCents)}/yr
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Category Breakdown (if available) */}
      {comparison?.categoryBreakdown && comparison.categoryBreakdown.length > 0 && (
        <Card className="mt-6">
          <CardContent className="p-6">
            <h3 className="font-semibold text-neutral-900 mb-4">By Category</h3>
            <div className="space-y-4">
              {comparison.categoryBreakdown.map(cat => (
                <div key={cat.category} className="flex items-center justify-between">
                  <span className="font-medium text-neutral-900">{cat.category}</span>
                  <div className="flex items-center gap-4 text-sm">
                    <span className="text-neutral-500">{formatCents(cat.current)}</span>
                    <ArrowRight className="w-4 h-4 text-neutral-400" />
                    <span className="text-neutral-900">{formatCents(cat.planned)}</span>
                    <span className={`font-medium ${
                      cat.delta > 0 ? 'text-red-600' : cat.delta < 0 ? 'text-emerald-600' : 'text-neutral-500'
                    }`}>
                      ({cat.delta > 0 ? '+' : ''}{formatCents(cat.delta)})
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Actions */}
      <div className="mt-6 flex justify-end gap-3">
        <Button
          variant="outline"
          onClick={() => navigate(`/holdings/planning/${id}`)}
          text="Back to Plan"
        />
        {plan.status === 'draft' && (
          <Button
            onClick={() => navigate(`/holdings/planning/${id}`)}
            icon={<CheckCircle className="w-4 h-4" />}
            text="Edit Plan"
          />
        )}
      </div>
    </div>
  );
}
